// fetchGoalStatus.js
'use strict';

import getMeData from '../selector/getMeData.js';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import getGoalData from '../selector/getGoalData.js';
import handleGoalProgressUpdated from '../action/handleGoalProgressUpdated.js';

/**
 * Fetch the latest status of a single goal from `/goals/:goalId/status` and
 * merge it back into the goal store.
 *
 * Used as a fallback for the toy-command countdown: when it reaches 0 the
 * client re-checks the goal so a stale command is removed even if the
 * `goal.progress.updated` pusher event was missed.
 * @kind action
 * @param {object} params
 * @param {string} params.goalId - goal id.
 * @returns {Promise} Action promise.
 * @see {@link https://api.docs.swag.live/#tag/leaderboards/operation/goals_goal_id_status__get|Get goal status API}
 * @example
 * dispatch(fetchGoalStatus({ goalId: 'abc123' }));
 */
const fetchGoalStatus =
  ({ goalId } = {}) =>
  async (dispatch, getState) => {
    if (!goalId) {
      return dispatch({ type: '' });
    }

    const networkingSelectPath = ['fetch', 'goalStatus', goalId];
    const token = getMeData(getState(), 'token');
    const headers = { ...getHeaders() };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const fetchOptions = {
      method: 'GET',
      headers,
    };
    const fetchUrl = getResourceUrl({
      endpoint: `/goals/${goalId}/status`,
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);
      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }
      const payload = await response.json();
      const existingGoal = getGoalData(getState(), [payload.id]) || {};

      dispatch(
        handleGoalProgressUpdated({
          ...existingGoal,
          goalId: payload.id,
          streamId: existingGoal.context.streamer_id,
          progress: payload.progress,
          context: {
            ...existingGoal.context,
            paused: payload.paused,
          },
        })
      );
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchGoalStatus;
